import './GroupOperationDialog.scss';
import { FC, ReactNode, useEffect, useId, useMemo, useRef, useState } from 'react';
import { SegmentedControl, VisuallyHidden } from '@mantine/core';
import {
    MdCancel,
    MdCheckCircle,
    MdContentCopy,
    MdInfoOutline,
    MdRemoveCircleOutline,
    MdWarning,
} from 'react-icons/md';
import { clsx } from 'clsx';
import Modal from '../../atoms/Modal';
import TraceJournalLink from '../../atoms/TraceJournalLink';
import Button from '../../atoms/Button';
import StatusTag from '../../atoms/StatusTag';
import type { StatusTagTone } from '../../atoms/StatusTag';
import Hint from '../../atoms/Hint';
import Skeleton from '../../atoms/Skeleton';
import DataTable from '../../data-table';
import type { IDataTableColumn } from '../../data-table';
import type {
    GroupOperationItemStatus,
    GroupOperationPhase,
    IGroupOperationDialogProps,
    IGroupOperationItem,
} from './GroupOperationDialog.types';
import {
    countGroupOperation,
    countWithNoun,
    DEFAULT_NOUN_FORMS,
    filterGroupOperationItems,
    GroupOperationFilter,
    groupOperationCopyText,
    IGroupOperationCounts,
    itemTitle,
    sortGroupOperationItems,
    statusText,
} from './groupOperationText';

const PREVIEW_NOTE =
    'Условия проверены заранее; хуки и маршрут могут дать другой итог — он будет показан после выполнения.';

/** С этого числа строк над таблицей — поиск: в длинном списке нужную строку не найти глазами. */
const SEARCH_FROM = 12;

/** Таблица не выше половины экрана: сводка и кнопки окна остаются на месте. */
const TABLE_MAX_HEIGHT = 'min(440px, 50dvh)';

type RowTone = 'ok' | 'warning' | 'blocked' | 'skipped';

const rowTone = (status: GroupOperationItemStatus, phase: GroupOperationPhase): RowTone => {
    if (status === 'warning') return 'warning';
    if (status === 'failed') return 'blocked';
    if (status === 'blocked') return phase === 'result' ? 'skipped' : 'blocked';
    return 'ok';
};

const TONE_TAG: Record<RowTone, StatusTagTone> = {
    ok: 'success',
    warning: 'warning',
    blocked: 'danger',
    skipped: 'neutral',
};

const TONE_ICON: Record<RowTone, ReactNode> = {
    ok: <MdCheckCircle />,
    warning: <MdWarning />,
    blocked: <MdCancel />,
    skipped: <MdRemoveCircleOutline />,
};

/** Подпись метки в колонке «Статус»: «Выполнится» сказано сводкой, метке — короткое слово. */
const tagText = (status: GroupOperationItemStatus, phase: GroupOperationPhase) =>
    status === 'warning' ? 'С предупреждением' : statusText(status, phase);

const clipboardCopy =
    typeof navigator !== 'undefined' && navigator.clipboard
        ? (text: string) => navigator.clipboard.writeText(text)
        : undefined;

type CopyState = 'idle' | 'copied' | 'failed';

const COPY_LABEL: Record<CopyState, string> = {
    idle: 'Скопировать список',
    copied: 'Список скопирован',
    failed: 'Не удалось скопировать',
};

const capitalize = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

interface IStat {
    key: string;
    label: string;
    value: number;
    tone: RowTone | 'total';
}

/** Сводка: сколько выбрано и чем кончится — крупные числа, статус подписью и значком, не только цветом. */
const statsOf = (
    counts: IGroupOperationCounts,
    phase: GroupOperationPhase,
    selected: number,
    canSeeWarnings: boolean,
): IStat[] => {
    const total: IStat = { key: 'total', label: 'Выбрано', value: selected, tone: 'total' };
    if (phase === 'result') {
        return [
            total,
            { key: 'success', label: 'Выполнено', value: counts.success, tone: 'ok' },
            { key: 'failed', label: 'Не выполнено', value: counts.failed, tone: 'blocked' },
            ...(counts.blocked > 0
                ? [{ key: 'skipped', label: 'Не запускалось', value: counts.blocked, tone: 'skipped' as const }]
                : []),
        ];
    }
    return [
        total,
        { key: 'runnable', label: 'Выполнится', value: counts.runnable, tone: 'ok' },
        { key: 'blocked', label: 'Нельзя', value: counts.blocked, tone: 'blocked' },
        ...(canSeeWarnings && counts.warning > 0
            ? [{ key: 'warning', label: 'С предупреждением', value: counts.warning, tone: 'warning' as const }]
            : []),
    ];
};

const GroupOperationStats: FC<{ stats: IStat[]; id: string }> = ({ stats, id }) => (
    <div className="rf-group-op__stats" id={id} role="status" data-testid="group-operation-summary">
        {stats.map((stat) => (
            <div
                key={stat.key}
                className={clsx(
                    'rf-group-op__stat',
                    `rf-group-op__stat--${stat.tone}`,
                    stat.value === 0 && 'rf-group-op__stat--zero',
                )}
            >
                <span className="rf-group-op__stat-label">
                    {stat.tone !== 'total' && (
                        <span className="rf-group-op__stat-icon" aria-hidden="true">
                            {TONE_ICON[stat.tone]}
                        </span>
                    )}
                    {stat.label}
                </span>{' '}
                <span className="rf-group-op__stat-value">{stat.value.toLocaleString('ru-RU')}</span>
            </div>
        ))}
    </div>
);

const StatsSkeleton: FC = () => (
    <div className="rf-group-op__stats" aria-hidden="true">
        {[0, 1, 2].map((index) => (
            <div key={index} className="rf-group-op__stat">
                <Skeleton height={14} width={72} radius="sm" />
                <Skeleton height={28} width={40} radius="sm" />
            </div>
        ))}
    </div>
);

const ReasonCell: FC<{ item: IGroupOperationItem }> = ({ item }) => {
    const messages = item.messages?.filter(Boolean) ?? [];
    if (messages.length === 0 && !item.traceId) return null;
    return (
        <span className="rf-group-op__reason">
            {messages.length > 0 && <span className="rf-group-op__reason-text">{messages.join(' ')}</span>}
            {item.traceId && (
                <span className="rf-group-op__trace">
                    Код обращения: <code>{item.traceId}</code> <TraceJournalLink traceId={item.traceId} />
                </span>
            )}
        </span>
    );
};

const filterLabel = (text: string, count: number) => (
    <span className="rf-group-op__filter-label">
        {text} <span className="rf-group-op__filter-count">{count.toLocaleString('ru-RU')}</span>
    </span>
);

/**
 * Групповая операция: предпросмотр «выполнится / нельзя — причина» по выбранным объектам и построчный итог
 * выполнения в том же окне. Окно не выполняет действие само: подтверждение отдаёт `onConfirm`.
 *
 * Окно стоит у верхнего края (`align="top"`): фазы и фильтр меняют высоту, а шапка и сводка остаются на месте.
 */
const GroupOperationDialog: FC<IGroupOperationDialogProps> = ({
    opened,
    actionName,
    nounForms = DEFAULT_NOUN_FORMS,
    phase,
    items,
    selectedCount,
    groupErrors,
    loadError,
    onConfirm,
    onRetryLoad,
    onClose,
    onRetryFailed,
    onSelectFailed,
    onCopy = clipboardCopy,
}) => {
    const summaryId = useId();
    const [filter, setFilter] = useState<GroupOperationFilter>('all');
    const [copyState, setCopyState] = useState<CopyState>('idle');
    const tableBoxRef = useRef<HTMLDivElement>(null);
    /** Высота таблицы со всеми строками: фильтр и поиск сужают список, но окно не прыгает. */
    const [lockedHeight, setLockedHeight] = useState<number>();

    useEffect(() => {
        setFilter('all');
        setCopyState('idle');
        setLockedHeight(undefined);
    }, [phase]);

    const counts = useMemo(() => countGroupOperation(items), [items]);
    const sorted = useMemo(() => sortGroupOperationItems(items), [items]);
    const visible = useMemo(() => filterGroupOperationItems(sorted, filter, phase), [sorted, filter, phase]);

    const isResult = phase === 'result';
    const isLoading = phase === 'loading';
    const problems = filterGroupOperationItems(items, 'problems', phase).length;
    const passing = filterGroupOperationItems(items, 'done', phase).length;
    const showFilter = !isLoading && !loadError && problems > 0 && passing > 0;
    const showSearch = !isLoading && items.length >= SEARCH_FROM;
    const selected = selectedCount ?? counts.total;

    const header = `${actionName} — ${countWithNoun(selected, nounForms)}`;

    const lockHeight = () => {
        if (lockedHeight === undefined && tableBoxRef.current) setLockedHeight(tableBoxRef.current.offsetHeight);
    };

    const columns = useMemo<IDataTableColumn<IGroupOperationItem>[]>(
        () => [
            {
                field: 'status',
                header: 'Статус',
                width: 168,
                minWidth: 168,
                allowSorting: false,
                allowFiltering: false,
                allowResizing: false,
                allowHiding: false,
                accessor: (item) => statusText(item.status, phase),
                render: (item) => {
                    const tone = rowTone(item.status, phase);
                    return (
                        <StatusTag tone={TONE_TAG[tone]} icon={TONE_ICON[tone]}>
                            {tagText(item.status, phase)}
                        </StatusTag>
                    );
                },
            },
            {
                field: 'title',
                header: capitalize(nounForms[0]),
                minWidth: 200,
                flexWeight: 1.4,
                wrapMode: 'nowrap',
                allowFiltering: false,
                allowHiding: false,
                accessor: itemTitle,
            },
            {
                field: 'reason',
                header: isResult ? 'Сообщение' : 'Причина',
                minWidth: 220,
                flexWeight: 1,
                wrapMode: 'trim',
                allowSorting: false,
                allowFiltering: false,
                allowHiding: false,
                accessor: (item) => [...(item.messages ?? []), item.traceId ?? ''].filter(Boolean).join(' '),
                render: (item) => <ReasonCell item={item} />,
            },
        ],
        [isResult, nounForms, phase],
    );

    const copy = (write: (text: string) => void | Promise<void>) => {
        const text = groupOperationCopyText(actionName, items, phase);
        // Исполнитель промиса вызывает запись сразу и превращает её синхронный сбой в отказ
        void new Promise<void>((resolve) => resolve(write(text))).then(
            () => setCopyState('copied'),
            () => setCopyState('failed'),
        );
    };

    const canCopy = !!onCopy && !isLoading && !loadError && items.length > 0;

    const footer = (
        <div className="modal-dlg-buttons rf-group-op__buttons">
            {canCopy && (
                <Button
                    className="rf-group-op__copy"
                    variant="text"
                    tone="neutral"
                    size="sm"
                    startAdornment={<MdContentCopy />}
                    onClick={() => copy(onCopy)}
                >
                    {COPY_LABEL[copyState]}
                </Button>
            )}
            <div className="rf-group-op__actions">
                {isResult ? (
                    <>
                        {counts.failed > 0 && onSelectFailed && (
                            <Button variant="light" size="sm" onClick={onSelectFailed}>
                                Выделить неудачные
                            </Button>
                        )}
                        {counts.failed > 0 && onRetryFailed && (
                            <Button variant="light" size="sm" onClick={onRetryFailed}>
                                Повторить неудачные
                            </Button>
                        )}
                        <Button size="sm" data-autofocus onClick={onClose}>
                            Закрыть
                        </Button>
                    </>
                ) : loadError ? (
                    <>
                        <Button variant="light" size="sm" onClick={onClose}>
                            {onRetryLoad ? 'Отмена' : 'Закрыть'}
                        </Button>
                        {onRetryLoad && (
                            <Button size="sm" data-autofocus onClick={onRetryLoad}>
                                Повторить
                            </Button>
                        )}
                    </>
                ) : (
                    <>
                        <Button variant="light" size="sm" onClick={onClose}>
                            Отмена
                        </Button>
                        <Button
                            size="sm"
                            data-autofocus
                            disabled={isLoading || counts.runnable === 0}
                            onClick={onConfirm}
                        >
                            {isLoading || counts.runnable === 0
                                ? 'Выполнить'
                                : `Выполнить для ${counts.runnable.toLocaleString('ru-RU')}`}
                        </Button>
                    </>
                )}
            </div>
        </div>
    );

    const filterControl = showFilter && (
        <SegmentedControl
            className="rf-group-op__filter"
            size="xs"
            aria-label="Показать"
            value={filter}
            onChange={(value) => {
                lockHeight();
                setFilter(value);
            }}
            data={[
                { value: 'all', label: filterLabel('Все', items.length) },
                { value: 'problems', label: filterLabel(isResult ? 'Неудачные' : 'Проблемные', problems) },
                { value: 'done', label: filterLabel(isResult ? 'Выполненные' : 'Проходящие', passing) },
            ]}
        />
    );

    return (
        <Modal
            opened={opened}
            size="xl"
            align="top"
            className="rf-group-op"
            header={header}
            aria-describedby={loadError ? undefined : summaryId}
            onClose={onClose}
            footer={footer}
        >
            <div className="rf-group-op__body" aria-busy={isLoading || undefined}>
                {loadError ? (
                    <Hint tone="danger" role="alert" maxWidth="100%" title="Не удалось проверить выбранное">
                        {loadError}
                    </Hint>
                ) : (
                    <>
                        {isLoading ? (
                            <>
                                <VisuallyHidden id={summaryId}>Проверяем выбранные объекты</VisuallyHidden>
                                <StatsSkeleton />
                            </>
                        ) : (
                            <GroupOperationStats id={summaryId} stats={statsOf(counts, phase, selected, !isResult)} />
                        )}

                        {groupErrors && groupErrors.length > 0 && (
                            <Hint tone="warning" maxWidth="100%" title="Групповое условие не выполнено">
                                {groupErrors.join(' ')}
                            </Hint>
                        )}

                        <div
                            ref={tableBoxRef}
                            className="rf-group-op__table"
                            style={lockedHeight ? { minHeight: lockedHeight } : undefined}
                        >
                            <DataTable<IGroupOperationItem>
                                columns={columns}
                                data={visible}
                                getRowId={(item) => item.id}
                                loading={isLoading}
                                paging={false}
                                fitColumns
                                overflowTooltip
                                showTableSettings={false}
                                showGlobalSearch={showSearch}
                                globalSearchMatch="contains"
                                onGlobalFilterChange={lockHeight}
                                toolbarStart={filterControl || undefined}
                                rowStatus={(item) => {
                                    const tone = rowTone(item.status, phase);
                                    return tone === 'blocked' ? 'error' : tone === 'warning' ? 'warning' : undefined;
                                }}
                                maxHeight={TABLE_MAX_HEIGHT}
                                emptyText="Нет строк"
                                data-testid="group-operation-items"
                            />
                        </div>

                        {!isResult && (
                            <p className="rf-group-op__note">
                                <MdInfoOutline className="rf-group-op__note-icon" aria-hidden="true" />
                                {PREVIEW_NOTE}
                            </p>
                        )}
                    </>
                )}
            </div>
        </Modal>
    );
};

export default GroupOperationDialog;
